import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "motion/react";

import { Button } from "../../components/Button/Button";
import OffShiftBadge from "../../components/OffShiftBadge/OffShiftBadge";
import useStationState from "../../hooks/useStationState";
import {
  Vaga,
  VagaTypeKey,
} from "../../services/stationState.types";
import VagaCard, { TYPE_ICON } from "./VagaCard";
import VagaEventImage from "./VagaEventImage";
import { deriveVagaMeta, FLOOR_GRID } from "./floorLayout";
import { formatClock, formatElapsed, formatIsoDateTime } from "./format";
import useMediaQuery from "../../hooks/useMediaQuery";
import styles from "./index.module.css";

// Largura mínima (px) da área útil do piso para caber o TEXTO dos cards de vaga.
// Abaixo disso os cards viram retângulos compactos. Ajustável.
const FLOOR_TEXT_MIN_WIDTH = 800;
// Abaixo disso o card fica estreito demais até para a HORA → mostra só a bolinha
// (fallback que evita a hora truncada no "meio termo" de resolução).
const FLOOR_DOT_ONLY_WIDTH = 460;

const TYPE_ORDER: VagaTypeKey[] = ["Operacao", "Supermercado", "Extra"];

const OccupancyMap: React.FC = () => {
  const { t, i18n } = useTranslation("occupancyMap");
  // Mesma convenção das telas de relatório do POC (locale do Intl, não o do i18next).
  const locale = i18n.language?.toLowerCase().startsWith("en") ? "en-US" : "pt-BR";
  // Compacta os cards quando o piso fica estreito demais para o texto — medindo a
  // largura REAL disponível (já considera sidebar/drawer, resumo lado a lado, etc.),
  // então a etapa dispara "sempre que fica apertado", em qualquer tamanho de tela.
  const floorRef = useRef<HTMLDivElement>(null);
  const [floorWidth, setFloorWidth] = useState(0);
  useLayoutEffect(() => {
    const el = floorRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      setFloorWidth(entries[0]?.contentRect.width ?? 0);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const compact = floorWidth > 0 && floorWidth < FLOOR_TEXT_MIN_WIDTH;
  // Tela pequena (celular): card mostra só a bolinha e o detalhe abre em bottom sheet.
  const isSmall = useMediaQuery("(max-width: 640px)");
  // Bolinha (sem a hora) em tela pequena OU quando o card fica estreito demais p/ a hora.
  const dotOnly = isSmall || (floorWidth > 0 && floorWidth < FLOOR_DOT_ONLY_WIDTH);
  const [paused, setPaused] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  // Trava o scroll do fundo enquanto o bottom sheet da vaga está aberto (mobile).
  useEffect(() => {
    if (!(isSmall && selectedKey)) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isSmall, selectedKey]);

  const { state, receivedAt, now, status, error } = useStationState(paused);

  const vagas = useMemo<Vaga[]>(() => state?.vagas ?? [], [state]);
  const hasData = vagas.length > 0;

  // Tempo decorrido interpolado localmente (o `now` congela quando pausado).
  const elapsedFor = (vaga: Vaga): number | null => {
    if (vaga.elapsedSeconds === null || vaga.elapsedSeconds === undefined) {
      return null;
    }
    const drift = Math.max(0, Math.floor((now - receivedAt) / 1000));
    return vaga.elapsedSeconds + drift;
  };

  const stateLabel = (raw: string): string =>
    raw === "Ocupada" ? t("occupied") : raw === "Livre" ? t("free") : raw;

  const typeOf = (vaga: Vaga): VagaTypeKey | null =>
    vaga.type ?? deriveVagaMeta(vaga.vagaKey).typeFromKey;

  const occupiedCount = vagas.filter((v) => v.state === "Ocupada").length;
  const freeCount = vagas.filter((v) => v.state === "Livre").length;

  const groups = useMemo(
    () =>
      TYPE_ORDER.map((type) => {
        const items = vagas.filter((v) => typeOf(v) === type);
        return {
          type,
          total: items.length,
          occupied: items.filter((v) => v.state === "Ocupada").length,
        };
      }).filter((g) => g.total > 0 || !hasData),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [vagas, hasData]
  );

  const selectedVaga = selectedKey
    ? vagas.find((v) => v.vagaKey === selectedKey) ?? null
    : null;

  // Indicador "ao vivo" reflete a conexão (e o modo pausado).
  const liveInfo = paused
    ? { label: t("paused"), cls: styles.livePillPaused }
    : status === "connected"
    ? { label: t("live"), cls: "" }
    : status === "disconnected"
    ? { label: t("offline"), cls: styles.livePillOff }
    : { label: t("reconnecting"), cls: styles.livePillWarn };

  const renderVaga = (vaga: Vaga): React.ReactNode => {
    const type = typeOf(vaga);
    const cell = FLOOR_GRID[vaga.vagaKey];
    return (
      <VagaCard
        key={vaga.vagaKey}
        data={{
          vagaKey: vaga.vagaKey,
          type,
          state: vaga.state,
          // No card compacto o texto vive num retângulo de ~40px com ellipsis —
          // ali a duração com dias vai sem os segundos ("2D 10:17").
          elapsedText: formatElapsed(elapsedFor(vaga), t, { compact }),
          hasEvent:
            vaga.elapsedSeconds !== null && vaga.elapsedSeconds !== undefined,
        }}
        typeLabel={type ? t(`type.${type}`) : deriveVagaMeta(vaga.vagaKey).name}
        stateLabel={stateLabel(vaga.state)}
        selected={selectedKey === vaga.vagaKey}
        compact={compact}
        dotOnly={dotOnly}
        style={
          cell
            ? { gridColumn: cell.gridColumn, gridRow: cell.gridRow }
            : undefined
        }
        onClick={() =>
          setSelectedKey((prev) =>
            prev === vaga.vagaKey ? null : vaga.vagaKey
          )
        }
      />
    );
  };

  // Linhas de detalhe da vaga (reutilizadas no painel inline do desktop e no bottom sheet mobile).
  const renderDetailRows = (vaga: Vaga): React.ReactNode => (
    <>
      <div className={styles.detailRow}>
        <span className={styles.detailKey}>{t("legendType")}</span>
        <span>{typeOf(vaga) ? t(`type.${typeOf(vaga)}`) : vaga.vagaKey}</span>
      </div>
      <div className={styles.detailRow}>
        <span className={styles.detailKey}>{t("legendOccupancy")}</span>
        <span className={vaga.state === "Ocupada" ? styles.detailOccupied : styles.detailFree}>
          {stateLabel(vaga.state)}
        </span>
      </div>
      <div className={styles.detailRow}>
        <span className={styles.detailKey}>{t("detailInState")}</span>
        <span>{formatElapsed(elapsedFor(vaga), t)}</span>
      </div>
      <div className={styles.detailRow}>
        <span className={styles.detailKey}>{t("detailSinceLabel")}</span>
        <span className={styles.detailValueNowrap}>
          {formatIsoDateTime(vaga.since, locale)}
        </span>
      </div>
      <div className={styles.detailImageBlock}>
        <span className={styles.detailKey}>{t("lastEventImage")}</span>
        <VagaEventImage key={vaga.vagaKey} vagaKey={vaga.vagaKey} version={vaga.since} />
      </div>
    </>
  );

  return (
    <div className={styles.page}>
      {/* ============================= HEADER ============================= */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>
            {t("title", { station: t("station") })}
          </h1>
          <div className={styles.subRow}>
            <span className={`${styles.livePill} ${liveInfo.cls}`}>
              <span className={styles.liveDot} aria-hidden="true" />
              {liveInfo.label}
            </span>
            <span className={styles.subMuted}>{t("updatedRealtime")}</span>
            <span className={styles.stationChip}>
              <span className="material-symbols-outlined" aria-hidden="true">
                home_work
              </span>
              {t("stationChip", { station: t("station") })}
            </span>
            <OffShiftBadge />
          </div>
        </div>

        <div className={styles.headerRight}>
          <div className={styles.countsBox}>
            <span className={styles.countPill}>
              <span
                className={`${styles.countDot} ${styles.countDotOccupied}`}
                aria-hidden="true"
              />
              <strong>{occupiedCount}</strong>
              <span className={styles.countLabel}>{t("occupiedShort")}</span>
            </span>
            <span className={styles.countDivider} aria-hidden="true" />
            <span className={styles.countPill}>
              <span
                className={`${styles.countDot} ${styles.countDotFree}`}
                aria-hidden="true"
              />
              <strong>{freeCount}</strong>
              <span className={styles.countLabel}>{t("freeShort")}</span>
            </span>
          </div>
          <Button
            label={paused ? t("resume") : t("pause")}
            icon={paused ? "play_arrow" : "pause"}
            severity="neutral"
            variant="outlined"
            onClick={() => setPaused((p) => !p)}
          />
        </div>
      </div>

      {error && <div className={styles.errorBanner}>{error}</div>}

      {/* ============================= MAIN GRID ============================= */}
      <div className={styles.mainGrid}>
        {/* -------- Floor (left) -------- */}
        <section className={styles.floorCard}>
          <div className={styles.cardHead}>
            <span className={styles.cardTitle}>
              <span className="material-symbols-outlined" aria-hidden="true">
                grid_view
              </span>
              {t("floorTitle")}
            </span>
            <span className={styles.cardMeta}>
              {t("updatedAt", {
                time: hasData ? formatClock(receivedAt) : "—",
              })}
            </span>
          </div>

          <div className={styles.floor} ref={floorRef}>
            <span className={`${styles.floorLabel} ${styles.labelFundo}`}>
              {t("fundo")}
            </span>
            <span className={`${styles.floorLabel} ${styles.labelFrente}`}>
              {t("frente")}
            </span>
            <span className={`${styles.floorLabel} ${styles.labelLateral}`}>
              {t("lateral")}
            </span>

            {hasData ? (
              <div className={`${styles.floorGrid} ${compact ? styles.floorGridMobile : ""}`}>
                {vagas.map((v) => renderVaga(v))}
              </div>
            ) : (
              <div className={styles.floorEmpty}>
                {status === "disconnected" ? t("offline") : t("loading")}
              </div>
            )}
          </div>

          {/* -------- Legend -------- */}
          <div className={styles.legend}>
            <div className={styles.legendGroup}>
              <span className={styles.legendLabel}>{t("legendOccupancy")}</span>
              <span className={styles.legendItem}>
                <span
                  className={`${styles.legendDot} ${styles.legendDotFree}`}
                  aria-hidden="true"
                />
                {t("free")}
              </span>
              <span className={styles.legendItem}>
                <span
                  className={`${styles.legendDot} ${styles.legendDotOccupied}`}
                  aria-hidden="true"
                />
                {t("occupied")}
              </span>
            </div>
            <div className={styles.legendGroup}>
              <span className={styles.legendLabel}>{t("legendType")}</span>
              <span className={styles.legendItem}>
                <span
                  className={`${styles.legendSquare} ${styles.legendSquareElevador}`}
                  aria-hidden="true"
                />
                {t("type.Operacao")}
              </span>
              <span className={styles.legendItem}>
                <span
                  className={`${styles.legendSquare} ${styles.legendSquareSuper}`}
                  aria-hidden="true"
                />
                {t("type.Supermercado")}
              </span>
              <span className={styles.legendItem}>
                <span
                  className={`${styles.legendSquare} ${styles.legendSquareExtra}`}
                  aria-hidden="true"
                />
                {t("type.Extra")}
              </span>
            </div>
          </div>
        </section>

        {/* -------- Summary (right) -------- */}
        <aside className={styles.summaryCard}>
          <h2 className={styles.summaryTitle}>{t("summaryTitle")}</h2>

          {groups.map((g) => (
            <div key={g.type} className={styles.groupRow}>
              <span className={styles.groupIcon}>
                <span className="material-symbols-outlined" aria-hidden="true">
                  {TYPE_ICON[g.type]}
                </span>
              </span>
              <span className={styles.groupInfo}>
                <span className={styles.groupName}>{t(`group.${g.type}`)}</span>
                <span className={styles.groupSub}>
                  {g.total} {t("vagas")}
                </span>
              </span>
              <span className={styles.groupCount}>
                <span className={styles.groupCountValue}>
                  {g.occupied} / {g.total}
                </span>
                <span className={styles.groupCountLabel}>
                  {t("occupiedLabel")}
                </span>
              </span>
            </div>
          ))}

          {selectedVaga && !isSmall ? (
            <div className={styles.detailBox}>
              <div className={styles.detailHead}>
                <span className={styles.detailName}>
                  {deriveVagaMeta(selectedVaga.vagaKey).name}
                </span>
                <span className={styles.detailCode}>
                  {deriveVagaMeta(selectedVaga.vagaKey).code}
                </span>
              </div>
              {renderDetailRows(selectedVaga)}
            </div>
          ) : (
            <div className={styles.hintBox}>
              <span className="material-symbols-outlined" aria-hidden="true">
                ads_click
              </span>
              {t("selectHint")}
            </div>
          )}
        </aside>
      </div>

      {/* Bottom sheet do detalhe da vaga — só no mobile (tela pequena). Sobe de baixo
          com fundo escurecido, igual ao drawer do menu lateral. */}
      <AnimatePresence>
        {isSmall && selectedVaga && (
          <>
            <motion.div
              className={styles.sheetBackdrop}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setSelectedKey(null)}
              aria-hidden="true"
            />
            <motion.div
              className={styles.sheet}
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "tween", duration: 0.25, ease: "easeOut" }}
              role="dialog"
              aria-modal="true"
            >
              <span className={styles.sheetHandle} aria-hidden="true" />
              <div className={styles.sheetHeader}>
                <span className={styles.sheetTitle}>
                  {deriveVagaMeta(selectedVaga.vagaKey).name}
                  <span className={styles.sheetCode}>
                    {deriveVagaMeta(selectedVaga.vagaKey).code}
                  </span>
                </span>
                <button
                  type="button"
                  className={styles.sheetClose}
                  onClick={() => setSelectedKey(null)}
                  aria-label={t("close", "Fechar")}
                >
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>
              <div className={styles.sheetBody}>{renderDetailRows(selectedVaga)}</div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default OccupancyMap;
